import { useState, useCallback, useEffect } from "react";
import { HTTPError } from "ky";
import { useLoginHistory } from "./useLoginHistory";
import {
  lookupEmail,
  sendVerificationCode,
  verifyCode,
  authenticate,
  getCompanies,
  selectCompany,
  searchChains,
  validateInvite,
  acceptInvite,
  sendPasswordReset,
  completeDeviceApproval,
  lookupDevice,
  probeResume,
  resumeContinue,
  resumeLogout,
  resumeSwitchUser,
} from "../lib/api";
import { addRecent } from "../lib/recentChains";
import { Company, AuthMethod } from "../schemas/auth";

const ACCESS_DENIED_MESSAGE =
  "You're not permissioned to access this application. Please contact your administrator.";

const SSO_ERROR_MESSAGES: Record<string, string> = {
  access_denied: ACCESS_DENIED_MESSAGE,
  no_access:
    "No access to any company for this login. Please contact your administrator.",
  sso_failed:
    "We couldn't complete your single sign-on. Please try again, or contact your administrator if it persists.",
  sso_config:
    "Single sign-on isn't configured correctly for this login. Please contact your administrator.",
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HTTPError && error.response.status === 429) {
    return "Too many attempts. Please wait a few minutes and try again.";
  }
  return fallback;
}

/**
 * True when an HTTP error body carries the OAuth `access_denied` error type
 * (the app-access gate denial). Discriminates on the body, not the 401 status,
 * which is ambiguous (also "not authenticated"). ZC-5724.
 */
async function isAccessDenied(error: unknown): Promise<boolean> {
  if (!(error instanceof HTTPError)) return false;
  try {
    const body = (await error.response.json()) as { error?: string };
    return body?.error === "access_denied";
  } catch {
    return false;
  }
}

/**
 * Stable per-company key for selection. portal_user_public_id can be null under
 * app-driven discovery, so we key on portal_uid + chain_code instead.
 */
export function companyKeyFor(company: Company): string {
  return `${company.portal_uid}:${company.chain_code}`;
}

/**
 * Compute the synchronous state patch for routing after a company list resolves.
 *
 * SUPPORT flow (ZC-5717): support companies carry is_support === true (set only
 * for the Zen Support IdP's synthesized chains by SsoCompany::toArray). Any
 * support identity — single or multi portal — goes straight to `chain-search`,
 * the single cross-portal chain typeahead, which REPLACES both the portal
 * chooser and the per-portal chain picker (the ZC-5658 two-step). No portal is
 * pre-selected and no upfront fetch runs; the typeahead searches across all
 * portals as the user types (see handleChainSearch + searchChains).
 *
 * We branch on is_support, NOT chain_code === -1 (master-chain admins
 * legitimately carry a -1 company in the password flow and must not be misrouted
 * into the gated support search).
 *
 * Non-support behaviour is unchanged: 1 portal → company-chooser, >1 →
 * portal-chooser.
 */
function routeAfterCompanies(companies: Company[]) {
  const portals = [...new Set(companies.map((c) => c.portal_uid))];
  const isSupportFlow = companies.some((c) => c.is_support === true);

  // SUPPORT flow (ZC-5717): a single cross-portal chain typeahead replaces BOTH
  // the portal chooser and the per-portal chain picker. Regardless of how many
  // portals the support identity spans, go straight to chain-search — the
  // typeahead searches chains across all portals at once (no portal-pick step,
  // no upfront fetch; results load as the user types).
  if (isSupportFlow) {
    return {
      companies,
      portals,
      selectedPortal: null,
      filteredCompanies: null,
      step: "chain-search" as const,
      loading: false,
      error: null,
    };
  }

  // Non-support behaviour is unchanged.
  if (portals.length > 1) {
    return {
      companies,
      portals,
      selectedPortal: null,
      filteredCompanies: null,
      step: "portal-chooser" as const,
      loading: false,
      error: null,
    };
  }

  return {
    companies,
    portals,
    selectedPortal: portals[0] ?? null,
    filteredCompanies: companies,
    step: "company-chooser" as const,
    loading: false,
    error: null,
  };
}

export type AuthStep =
  | "email"
  | "verification"
  | "method-selection"
  | "password"
  | "portal-chooser"
  | "company-chooser"
  | "chain-search"
  | "invite-loading"
  | "sso-loading"
  | "set-password"
  | "forgot-password"
  | "reset-sent"
  | "token-error"
  | "device-consent"
  | "device-approved"
  | "device-denied";

export interface AuthFlowState {
  step: AuthStep;
  email: string;
  identityId: string | null;
  verified: boolean;
  authMethods: AuthMethod[];
  companies: Company[] | null;
  portals: string[] | null;
  selectedPortal: string | null;
  filteredCompanies: Company[] | null;
  // ZC-5717: results of the cross-portal chain typeahead (support flow). null
  // before the first search; [] is a real "no matches" result.
  searchResults: Company[] | null;
  searchLoading: boolean;
  error: string | null;
  loading: boolean;
  token: string | null;
  tokenType: "invite" | "password_reset" | null;
  tokenExpired: boolean;
  companyName: string | null;
  passwordResetSuccess: boolean;
  // ZC-5706: set when the user arrived via ?logged_out=1 (returned here by the
  // in-app logout for Auth-originated sessions), so the email screen can show a
  // "you've been signed out" notice.
  loggedOut: boolean;
  /**
   * The encrypted OAuth authorization request payload carried through the SSO
   * multi-company round-trip. The backend includes it in the sso=complete
   * redirect; the SPA stores it here so handleCompanySelect can forward it to
   * selectCompany (completing Flow B for SSO multi-membership users).
   */
  ssoAuthRequest: string;
  /**
   * Device flow (RFC 8628): the client name requesting device approval.
   */
  deviceClientName: string | null;
  /**
   * Device flow (RFC 8628): the scopes the client is requesting.
   */
  deviceScopes: string[];
  /**
   * Device flow (RFC 8628): the selected company details for device approval.
   * Carries portal_uid and chain_code for the approve call.
   */
  deviceSelectedCompany: Company | null;
  /**
   * Resume session (ZC-5707): true when a resumable session was detected by
   * the probe on mount. When true, the email screen shows the ResumeCard.
   */
  resumable: boolean;
  /**
   * The human-readable identity label returned by the resume probe (e.g. email
   * address). Null when no resume session is present.
   */
  resumeLabel: string | null;
  /**
   * The identity ID returned by the resume probe (ZC-5707). Stored separately
   * from identityId (which is only set when actually continuing) so
   * handleResumeContinue has the id available when it calls routeAfterCompanies,
   * mirroring the SSO path which captures ssoIdentityId before setState.
   */
  resumeIdentityId: string | null;
}

const initialState: AuthFlowState = {
  step: "email",
  email: "",
  identityId: null,
  verified: false,
  authMethods: [],
  companies: null,
  portals: null,
  selectedPortal: null,
  filteredCompanies: null,
  searchResults: null,
  searchLoading: false,
  error: null,
  loading: false,
  token: null,
  tokenType: null,
  tokenExpired: false,
  companyName: null,
  passwordResetSuccess: false,
  loggedOut: false,
  ssoAuthRequest: "",
  deviceClientName: null,
  deviceScopes: [],
  deviceSelectedCompany: null,
  resumable: false,
  resumeLabel: null,
  resumeIdentityId: null,
};

/**
 * Append auth_request, return_to, and callback_url as query parameters to an
 * SSO start redirect URL. Parameters are only appended when non-empty; when
 * authRequest is empty (legacy path) the URL is returned unchanged.
 */
function buildSsoRedirectUrl(
  redirectUrl: string,
  authRequest: string,
  returnTo: string,
  callbackUrl: string
): string {
  if (!authRequest && !returnTo && !callbackUrl) return redirectUrl;
  const params: string[] = [];
  let separator = redirectUrl.includes("?") ? "&" : "?";

  if (authRequest) {
    params.push(`auth_request=${encodeURIComponent(authRequest)}`);
  }
  if (returnTo) {
    params.push(`return_to=${encodeURIComponent(returnTo)}`);
  }
  if (callbackUrl) {
    params.push(`callback_url=${encodeURIComponent(callbackUrl)}`);
  }

  return `${redirectUrl}${separator}${params.join("&")}`;
}

/**
 * @param authRequest The encrypted OAuth authorization request payload
 *   emitted by /oauth/authorize → /auth/login when this bundle is reached
 *   via the SPA login flow (Flow B). When non-empty, the three terminal
 *   API calls (authenticate, selectCompany, acceptInvite) forward it so
 *   the backend can hand it back to league/oauth2-server to complete the
 *   authorize and return a /auth/spa-callback URL. When empty, the bundle
 *   reaches the legacy /{portal}/auth/callback endpoint exactly as before.
 *
 *   For the sso=complete path (multi-company SSO callback), auth_request is
 *   read from the URL query params directly — the backend includes it in the
 *   redirect so it survives the SPA round-trip.
 *
 * @param returnTo Portal login callback URL for the portal lane (ZC-3444).
 *   Threaded through the login SPA and forwarded to authenticate /
 *   selectCompany / acceptInvite.
 *
 * @param callbackUrl Portal login callback URL for the portal lane (ZC-3444).
 *   Threaded through the login SPA and forwarded to authenticate /
 *   selectCompany / acceptInvite.
 */
export function useAuthFlow(authRequest = "", returnTo = "", callbackUrl = "") {
  const [state, setState] = useState<AuthFlowState>(initialState);

  // Read device flow config from window.__AUTH_CONFIG__ (set by the device verification page)
  const authConfig = (window as Record<string, unknown>).__AUTH_CONFIG__ as
    | { flow?: string; user_code?: string; [key: string]: unknown }
    | undefined;
  const deviceFlow = authConfig?.flow === "device";
  const userCode = (authConfig?.user_code as string) ?? "";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const ssoErrorCode = params.get("error");
    if (ssoErrorCode) {
      const message = SSO_ERROR_MESSAGES[ssoErrorCode] ?? null;
      // Strip ?error so a refresh doesn't re-show it.
      params.delete("error");
      const nextSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname +
          (nextSearch ? `?${nextSearch}` : "") +
          window.location.hash
      );
      if (message) {
        setState((prev) => ({
          ...prev,
          step: "email",
          loading: false,
          error: message,
        }));
        return;
      }
    }

    const ssoComplete = params.get("sso") === "complete";
    const ssoIdentityId = params.get("identity_id");
    if (ssoComplete && ssoIdentityId) {
      // Flow B: the backend may include auth_request in the sso=complete
      // redirect so the SPA can forward it to selectCompany. Read it here
      // and prefer it over the prop (which is empty when coming from the
      // SSO path — the OAuth payload is in the URL, not __AUTH_CONFIG__).
      const ssoAuthRequest = params.get("auth_request") ?? "";

      setState((prev) => ({
        ...prev,
        step: "sso-loading",
        identityId: ssoIdentityId,
        loading: true,
        error: null,
        // Store the SSO round-trip auth_request so handleCompanySelect
        // can forward it to the select-company API call.
        ...(ssoAuthRequest !== "" ? { ssoAuthRequest } : {}),
      }));

      getCompanies(ssoIdentityId)
        .then((companies) => {
          if (companies.length === 0) {
            setState((prev) => ({
              ...prev,
              step: "email",
              loading: false,
              error:
                "We couldn't find any companies for your account. Please contact support.",
            }));
            return;
          }
          const routePatch = routeAfterCompanies(companies);
          setState((prev) => ({
            ...prev,
            identityId: ssoIdentityId,
            verified: true,
            ...routePatch,
          }));
        })
        .catch(() => {
          setState((prev) => ({
            ...prev,
            step: "email",
            loading: false,
            error: "Your sign-in session has expired. Please sign in again.",
          }));
        });
      return;
    }

    // ZC-5706: the in-app logout returns Auth-originated users here with
    // ?logged_out=1. Surface a notice on the email screen (the default step).
    if (params.get("logged_out") === "1") {
      setState((prev) => ({ ...prev, loggedOut: true }));
    }

    const inviteToken = params.get("invite");
    const resetToken = params.get("reset");
    const token = inviteToken ?? resetToken;

    if (!token) return;

    setState((prev) => ({ ...prev, step: "invite-loading", token }));

    validateInvite(token)
      .then((result) => {
        if (result.valid) {
          setState((prev) => ({
            ...prev,
            step: "set-password",
            email: result.email,
            tokenType: result.type,
            companyName: result.company_name,
            loading: false,
            error: null,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            step: "token-error",
            tokenExpired: result.expired,
            email: result.email ?? "",
            loading: false,
            error: null,
          }));
        }
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          step: "token-error",
          tokenExpired: false,
          loading: false,
          error: null,
        }));
      });
  }, []);

  // Resume probe (ZC-5707): only fires when there are no special URL params
  // (invite/reset/sso=complete) — those flows are handled by the mount effect
  // above and the probe must not race with them. We mirror the same guard by
  // checking the same URL params. Any probe error is swallowed silently so it
  // can never block login.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasSpecialFlow =
      (params.get("sso") === "complete" &&
        params.get("identity_id") !== null) ||
      params.get("invite") !== null ||
      params.get("reset") !== null ||
      deviceFlow;

    if (hasSpecialFlow) return;

    let cancelled = false;
    probeResume()
      .then((result) => {
        if (cancelled) return;
        if (result.resumable) {
          setState((prev) => ({
            ...prev,
            resumable: true,
            resumeLabel: result.identityLabel,
            resumeIdentityId: result.identityId,
          }));
        }
        // resumable:false → leave defaults (false / null)
      })
      .catch(() => {
        // Swallow any error — probe failure must never block login
      });

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ordering constraint (ZC-5958): this call MUST come after the two mount
  // useEffects above. useLoginHistory's own mount effect seeds history state
  // via replaceState({ zenAuthIdx: 0 }) — it must run AFTER the ?error-stripping
  // replaceState in the first mount effect so the index seed wins (effects run
  // in hook-call/declaration order, and both are mount effects).
  const { back, restart, markCompleted } = useLoginHistory(state, setState);

  const setLoading = (loading: boolean) =>
    setState((prev) => ({ ...prev, loading, error: null }));

  const setError = (error: string) =>
    setState((prev) => ({ ...prev, loading: false, error }));

  const handleEmailSubmit = useCallback(
    async (email: string, turnstileToken: string) => {
      setLoading(true);

      try {
        const result = await lookupEmail(email, turnstileToken);

        const methods = result.auth_methods ?? [];
        // ZC-5716: support identities must always authenticate via the Zen Support
        // IdP. Its method (source: "support") is returned only for support
        // identities (ZC-5604), alongside their password + other IdPs. Collapse to
        // it so the single-SSO-no-password redirect below sends them straight there;
        // password + other IdPs are filtered out before any step/state is set.
        const supportMethods = methods.filter((m) => m.source === "support");
        const effectiveMethods =
          supportMethods.length > 0 ? supportMethods : methods;
        const ssoMethods = effectiveMethods.filter((m) => m.type === "sso");
        const hasPassword = effectiveMethods.some((m) => m.type === "password");

        // Single SSO, no password — redirect immediately
        if (
          ssoMethods.length > 0 &&
          !hasPassword &&
          effectiveMethods.length === ssoMethods.length
        ) {
          const ssoMethod = ssoMethods[0];
          if (ssoMethod?.redirect_url) {
            markCompleted();
            window.location.href = buildSsoRedirectUrl(
              ssoMethod.redirect_url,
              authRequest,
              returnTo,
              callbackUrl
            );
            return;
          }
        }

        if (!result.found || !result.verified) {
          // Always go to verification step regardless of whether the account
          // exists. This prevents user enumeration — an attacker cannot
          // distinguish "no account" from "unverified account".
          await sendVerificationCode(email);

          setState((prev) => ({
            ...prev,
            email,
            identityId: result.identity_id,
            authMethods: effectiveMethods,
            step: "verification",
            loading: false,
            error: null,
          }));
          return;
        }

        // User is found and verified — determine next step based on auth methods
        if (effectiveMethods.length > 1) {
          setState((prev) => ({
            ...prev,
            email,
            identityId: result.identity_id,
            verified: true,
            authMethods: effectiveMethods,
            step: "method-selection",
            loading: false,
            error: null,
          }));
          return;
        }

        setState((prev) => ({
          ...prev,
          email,
          identityId: result.identity_id,
          verified: true,
          authMethods: effectiveMethods,
          step: "password",
          loading: false,
          error: null,
        }));
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 422) {
          try {
            const body = await error.response.json();
            if (body.error === "TURNSTILE_FAILED") {
              setError(
                "Verification failed. Please refresh the page and try again."
              );
              return;
            }
          } catch {
            // JSON parse failed, fall through to generic error
          }
        }
        setError(
          getErrorMessage(error, "Something went wrong. Please try again.")
        );
      }
    },
    [markCompleted]
  );

  const handleMethodSelect = useCallback(
    (method: AuthMethod) => {
      if (method.type === "sso" && method.redirect_url) {
        markCompleted();
        window.location.href = buildSsoRedirectUrl(
          method.redirect_url,
          authRequest,
          returnTo,
          callbackUrl
        );
        return;
      }

      setState((prev) => ({
        ...prev,
        step: "password",
        error: null,
      }));
    },
    [authRequest, returnTo, callbackUrl, markCompleted]
  );

  const handleVerificationSubmit = useCallback(
    async (code: string) => {
      setLoading(true);

      try {
        const result = await verifyCode(state.email, code);

        if (!result.verified) {
          setError("Invalid verification code. Please try again.");
          return;
        }

        setState((prev) => ({
          ...prev,
          verified: true,
          step: "password",
          loading: false,
          error: null,
        }));
      } catch (error) {
        setError(
          getErrorMessage(error, "Invalid or expired code. Please try again.")
        );
      }
    },
    [state.email]
  );

  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      if (!state.identityId) {
        setError("Session expired. Please start again.");
        return;
      }

      setLoading(true);

      try {
        const result = await authenticate(
          state.identityId,
          password,
          authRequest,
          returnTo,
          callbackUrl
        );

        if (!result.authenticated) {
          setError("Incorrect password. Please try again.");
          return;
        }

        if (result.redirect_uri) {
          markCompleted();
          window.location.href = result.redirect_uri;
          return;
        }

        const companies = result.companies ?? [];

        if (companies.length > 1) {
          const routePatch = routeAfterCompanies(companies);
          setState((prev) => ({
            ...prev,
            ...routePatch,
          }));
          return;
        }

        setError("Authentication failed. Please try again.");
      } catch (error) {
        if (await isAccessDenied(error)) {
          setError(ACCESS_DENIED_MESSAGE);
          return;
        }
        setError(
          getErrorMessage(error, "Something went wrong. Please try again.")
        );
      }
    },
    [state.identityId, authRequest, returnTo, callbackUrl, markCompleted]
  );

  const handlePortalSelect = useCallback(
    async (portalUid: string) => {
      const portalCompanies = (state.companies ?? []).filter(
        (c) => c.portal_uid === portalUid
      );

      setState((prev) => ({
        ...prev,
        selectedPortal: portalUid,
        filteredCompanies: portalCompanies,
        step: "company-chooser",
        error: null,
      }));
    },
    [state.companies]
  );

  const handleCompanySelect = useCallback(
    async (company: Company) => {
      // ZC-5815: act on the company the user actually clicked. Every caller
      // (recents, the cross-portal search results, the company-chooser) renders
      // the same card and now hands us the full Company object, so we no longer
      // re-resolve a "portal_uid:chain_code" key against
      // searchResults/filteredCompanies/companies. That pool never included the
      // localStorage-backed recents, so clicking a recent for a chain the
      // support user holds no account in silently no-op'd.
      if (!state.identityId) return;

      setLoading(true);

      // DEVICE FLOW (RFC 8628): after selecting a company, fetch the device
      // consent details and transition to device-consent so the user can
      // explicitly approve or deny. The auth-code flow continues as before
      // (redirects to selectCompany's redirect_uri).
      if (deviceFlow && userCode) {
        try {
          // Fetch device client name and scopes
          const deviceInfo = await lookupDevice(userCode);
          setState((prev) => ({
            ...prev,
            step: "device-consent",
            deviceClientName: deviceInfo.client_name,
            deviceScopes: deviceInfo.scopes,
            deviceSelectedCompany: company,
            loading: false,
            error: null,
          }));
          return;
        } catch (error) {
          setError(
            getErrorMessage(
              error,
              "Failed to load device details. Please try again."
            )
          );
          return;
        }
      }

      // Auth-code flow (legacy path — unchanged)
      // For the SSO multi-company path, auth_request arrives via the URL query
      // params of the sso=complete redirect (stored in state.ssoAuthRequest).
      // For the password multi-company path, auth_request comes from the
      // authRequest prop (window.__AUTH_CONFIG__). Prefer the SSO value when
      // present so the correct payload is forwarded to selectCompany.
      const effectiveAuthRequest = state.ssoAuthRequest || authRequest;

      try {
        const result = await selectCompany(
          state.identityId,
          company.portal_uid,
          company.chain_code,
          effectiveAuthRequest,
          returnTo,
          callbackUrl
        );

        if (result.redirect_uri) {
          // ZC-5733: remember support chain selections so they surface under
          // "Recent chains" next time. Only the support search/recents flow —
          // not the normal company-chooser — and only after selectCompany
          // accepted it (so we never remember a chain the server rejected).
          if (company.is_support && state.identityId) {
            addRecent(state.identityId, {
              portal_uid: company.portal_uid,
              chain_code: company.chain_code,
              name: company.company_name ?? company.portal_uid,
            });
          }
          markCompleted();
          window.location.href = result.redirect_uri;
          return;
        }

        setError("Failed to select company. Please try again.");
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 403) {
          // The company is reachable via SSO but the user has no access there
          // and auto-provisioning is off — surface the backend's guidance.
          setError(
            "You don't have access to this company. Please contact your administrator."
          );
          return;
        }
        if (await isAccessDenied(error)) {
          setError(ACCESS_DENIED_MESSAGE);
          return;
        }
        setError(
          getErrorMessage(error, "Something went wrong. Please try again.")
        );
      }
    },
    [
      state.identityId,
      state.ssoAuthRequest,
      authRequest,
      returnTo,
      callbackUrl,
      deviceFlow,
      userCode,
      markCompleted,
    ]
  );

  // ZC-5717: cross-portal chain typeahead. Debounced by the ChainSearch
  // component; this performs the actual search call and stores results. A
  // short/empty query clears results (the backend also enforces a min length).
  const handleChainSearch = useCallback(
    async (query: string) => {
      const identityId = state.identityId;
      if (!identityId) return;

      const trimmed = query.trim();
      if (trimmed.length < 2) {
        setState((prev) => ({
          ...prev,
          searchResults: null,
          searchLoading: false,
        }));
        return;
      }

      setState((prev) => ({ ...prev, searchLoading: true, error: null }));

      try {
        const results = await searchChains(identityId, trimmed);
        setState((prev) => ({
          ...prev,
          searchResults: results,
          searchLoading: false,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          searchLoading: false,
          searchResults: [],
          error: getErrorMessage(
            error,
            "Couldn't search chains. Please try again."
          ),
        }));
      }
    },
    [state.identityId]
  );

  const handleDeviceApprove = useCallback(async () => {
    const company = state.deviceSelectedCompany;
    if (!company || !state.identityId || !userCode) return;

    setLoading(true);

    try {
      await completeDeviceApproval({
        user_code: userCode,
        identity_id: state.identityId,
        portal_uid: company.portal_uid,
        chain_code: company.chain_code,
        approved: true,
      });

      setState((prev) => ({
        ...prev,
        step: "device-approved",
        loading: false,
        error: null,
      }));
    } catch (error) {
      setError(
        getErrorMessage(
          error,
          "Failed to complete device approval. Please try again."
        )
      );
    }
  }, [state.deviceSelectedCompany, state.identityId, userCode]);

  const handleDeviceDeny = useCallback(async () => {
    const company = state.deviceSelectedCompany;
    if (!company || !state.identityId || !userCode) return;

    setLoading(true);

    try {
      await completeDeviceApproval({
        user_code: userCode,
        identity_id: state.identityId,
        portal_uid: company.portal_uid,
        chain_code: company.chain_code,
        approved: false,
      });

      setState((prev) => ({
        ...prev,
        step: "device-denied",
        loading: false,
        error: null,
      }));
    } catch (error) {
      setError(
        getErrorMessage(
          error,
          "Failed to process device denial. Please try again."
        )
      );
    }
  }, [state.deviceSelectedCompany, state.identityId, userCode]);

  const handleResendCode = useCallback(async () => {
    setLoading(true);

    try {
      await sendVerificationCode(state.email);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setError(
        getErrorMessage(error, "Failed to resend code. Please try again.")
      );
    }
  }, [state.email]);

  const handleSetPassword = useCallback(
    async (password: string) => {
      if (!state.token) {
        setError("Session expired. Please start again.");
        return;
      }

      setLoading(true);

      try {
        const result = await acceptInvite(
          state.token,
          password,
          authRequest,
          returnTo,
          callbackUrl
        );

        if (result.redirect_uri) {
          markCompleted();
          window.location.href = result.redirect_uri;
          return;
        }

        restart({
          ...initialState,
          passwordResetSuccess: true,
        });
      } catch (error) {
        setError(
          getErrorMessage(
            error,
            "This link is no longer valid. Please request a new one."
          )
        );
      }
    },
    [state.token, authRequest, returnTo, callbackUrl, markCompleted, restart]
  );

  const handleForgotPassword = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: "forgot-password" as AuthStep,
      error: null,
    }));
  }, []);

  const handleSendReset = useCallback(async (email: string) => {
    setLoading(true);

    try {
      await sendPasswordReset(email);
      setState((prev) => ({
        ...prev,
        step: "reset-sent" as AuthStep,
        email,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setError(
        getErrorMessage(error, "Something went wrong. Please try again.")
      );
    }
  }, []);

  const handleExpiredInviteReset = useCallback(async () => {
    if (!state.email) return;

    setLoading(true);

    try {
      await sendPasswordReset(state.email);
      setState((prev) => ({
        ...prev,
        step: "reset-sent" as AuthStep,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setError(
        getErrorMessage(error, "Something went wrong. Please try again.")
      );
    }
  }, [state.email]);

  /**
   * ZC-5707: The user clicked "Continue as {label}" on the ResumeCard.
   * Calls resume/continue, which returns the identity label + companies, then
   * routes to the chooser exactly the same way the SSO path does — via the
   * canonical routeAfterCompanies helper.
   *
   * On failure (e.g. 401 because the session expired between probe and click)
   * we swallow the error and drop back to a clean email screen (card gone).
   */
  const handleResumeContinue = useCallback(async () => {
    setLoading(true);

    // Capture the probe identity id before the async call so it's stored on the
    // resumed state — the cross-portal chain typeahead (ZC-5717) needs it to
    // query, just as the SSO path stores ssoIdentityId.
    const resumeIdentityId = state.resumeIdentityId;

    try {
      const result = await resumeContinue(authRequest);

      // App-scoped (non-tenant) client: the backend completed OAuth on the
      // per-identity app-access grant — there is no company to pick. Redirect
      // straight to the client callback, mirroring handlePasswordSubmit (ZC-5715).
      if ("redirectUri" in result) {
        markCompleted();
        window.location.href = result.redirectUri;
        return;
      }

      // routeAfterCompanies routes a support identity straight to the chain-search
      // typeahead (ZC-5717) and a normal identity to the company/portal chooser —
      // resume inherits that routing.
      const routePatch = routeAfterCompanies(result.companies);
      setState((prev) => ({
        ...prev,
        identityId: resumeIdentityId,
        resumeLabel: result.identityLabel,
        // Supersedes ZC-5957: back-navigation provenance is now recorded by the
        // history snapshot stack (useLoginHistory) rather than inferred from an
        // ssoOrigin flag, so no flag needs to be set here.
        ...routePatch,
      }));
    } catch {
      // Session expired between probe and click — drop the card, show email
      setState((prev) => ({
        ...prev,
        resumable: false,
        resumeLabel: null,
        loading: false,
        step: "email",
        error: null,
      }));
    }
  }, [state.resumeIdentityId, authRequest, markCompleted]);

  /**
   * ZC-5707: The user clicked × (forget this login). Calls resume/switch-user
   * to clear the session server-side, then drops the ResumeCard from the UI.
   * Errors are swallowed — the card is always dismissed regardless.
   */
  const handleSwitchUser = useCallback(async () => {
    try {
      await resumeSwitchUser();
    } catch {
      // Swallow — we dismiss the card regardless of the server response
    }
    setState((prev) => ({
      ...prev,
      resumable: false,
      resumeLabel: null,
    }));
  }, []);

  /**
   * ZC-5707: The user clicked "Log out" from the IdentityChip on a chooser
   * screen. Calls resume/logout (best-effort revoke — errors are swallowed),
   * then resets the SPA to a clean email step. This is the in-SPA logout path;
   * there is no hard navigation because the SPA IS the auth login page.
   */
  const handleLogout = useCallback(async () => {
    try {
      await resumeLogout();
    } catch {
      // Swallow — best-effort revoke; reset to email regardless
    }
    restart({ ...initialState });
  }, [restart]);

  // For ResetSent/TokenError's "back to sign in" affordance: a full reset to
  // the start of the flow, collapsing the history stack (unlike goBack, which
  // only pops one entry).
  const handleBackToSignIn = useCallback(() => {
    restart({ ...initialState });
  }, [restart]);

  return {
    state,
    handleEmailSubmit,
    handleMethodSelect,
    handleVerificationSubmit,
    handlePasswordSubmit,
    handlePortalSelect,
    handleCompanySelect,
    handleChainSearch,
    handleDeviceApprove,
    handleDeviceDeny,
    handleResendCode,
    handleSetPassword,
    handleForgotPassword,
    handleSendReset,
    handleExpiredInviteReset,
    // ZC-5958: Back is now a pure history traversal — popping the snapshot
    // stack restores the exact prior AuthFlowState (companies, verified
    // flags, entered email, etc.), so there is no per-step inference to
    // hand-roll here (see useLoginHistory's navModeFor + the recording
    // effect). This replaces the old goBack() switch, which re-derived
    // "where did the user come from" from flags (ssoOrigin,
    // authMethods.length, verified) and was the source of the
    // ZC-5901/ZC-5957 bug class.
    goBack: back,
    handleBackToSignIn,
    handleResumeContinue,
    handleSwitchUser,
    handleLogout,
  };
}
